import { useEffect, useId, useRef } from 'react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { SendHorizontalIcon, XIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import { Button } from '@rocco/components/button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage,
} from '@rocco/components/form';
import { Textarea } from '@rocco/components/textarea';
import { renameFile } from '~/modules/file';
import type { TextOrFileMessage } from '../../types';
import {
    validateChatUploadFile,
    validateChatUploadFileCount,
} from '../../utils/validation';
import { ChatInputReplyPreview } from './ChatInputReplyPreview';
import { ChatInputAttachments } from './ChatInputAttachments';
import { TypingIndicator } from './TypingIndicator';
import { GiphyPicker } from './GiphyPicker';

const formSchema = z.object({
    message: z
        .string()
        .trim()
        .max(2000, { message: 'Maximum 2000 characters' }),
    attachments: z.array(z.instanceof(File)),
});

/**
 * The text input for the chat.
 * - The chat text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatInput({
    className,
    conversationID,
    isOpen,
    isDisabled = false,
    isSubmitting = false,
    isNotParticipant = false,
    autoFocus = true,
    replyingTo,
    replyMode = 'reply',
    onCancelReply,
    onSend,
    onAttachmentsChanged,
    onTyping,
}: {
    className?: string;
    conversationID?: number;
    /**
     * Whether the chat box is open.
     */
    isOpen: boolean;
    /**
     * Whether the send button is disabled. Defaults to `false`.
     */
    isDisabled?: boolean;
    /**
     * Whether the form is submitting. Only used when sending a message with
     * file attachments. Defaults to `false`.
     */
    isSubmitting?: boolean;
    /**
     * The current user is not a participant of the conversation. If true, the
     * form will be disabled and the input will show a different placeholder message.
     */
    isNotParticipant?: boolean;
    /**
     * Auto focus the input when the chat box is opened. Defaults to `true`.
     */
    autoFocus?: boolean;
    /**
     * The message being replied to or forwarded. If set, a preview of the
     * message is shown.
     */
    replyingTo?: TextOrFileMessage | null;
    /**
     * The mode of the reply or forward feature. Defaults to `'reply'`.
     */
    replyMode?: 'reply' | 'forward';
    /**
     * Callback when the user cancels the reply.
     */
    onCancelReply?: () => void;
    /**
     * Callback when the user sends a message.
     */
    onSend?: ({
        message,
        attachments,
        replyTo,
    }: {
        message: string;
        attachments: File[];
        replyTo?: TextOrFileMessage;
    }) => void;
    /**
     * Callback when the attachments have changed.
     */
    onAttachmentsChanged?: () => void;
    /**
     * Callback when the user is typing. Called on every keystroke, so the
     * caller should throttle if needed.
     */
    onTyping?: () => void;
}) {
    const isForwarding = !!replyingTo && replyMode === 'forward';
    const formId = useId();
    const formRef = useRef<HTMLFormElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            message: '',
            attachments: [],
        },
    });
    const message = form.watch('message');
    const attachments = form.watch('attachments');
    const isSendDisabled =
        isDisabled ||
        isSubmitting ||
        isNotParticipant ||
        // When forwarding, text and attachments are optional.
        (!message.trim() && !attachments.length && !isForwarding);

    /**
     * Handle adding file attachments to the message.
     * @param files The files to add.
     * @param renamePastedImages Rename pasted images to include a timestamp.
     */
    function onFilesAdded(files: FileList | null, renamePastedImages: boolean) {
        // Disable file uploads when forwarding.
        if (!files?.length || isForwarding) {
            return;
        }
        // TODO: tracker
        const newAttachments = [...form.getValues('attachments')];
        const errors = new Set<string>();
        for (const file of files) {
            const isDuplicate = newAttachments.some(
                (f) =>
                    f.name === file.name &&
                    f.size === file.size &&
                    f.lastModified === file.lastModified
            );
            if (isDuplicate) {
                continue;
            }
            const isFileCountValid = validateChatUploadFileCount(
                newAttachments.length + 1
            );
            if (typeof isFileCountValid === 'object') {
                errors.add(isFileCountValid.message);
                break;
            }
            const validationResult = validateChatUploadFile(file);
            if (validationResult.valid) {
                // Pasted images have the filename "image.png". Rename them to
                // be more descriptive.
                if (renamePastedImages && file.name === 'image.png') {
                    newAttachments.push(renamePastedImage(file));
                } else {
                    newAttachments.push(file);
                }
            } else {
                errors.add(validationResult.message);
            }
        }
        form.setValue('attachments', newAttachments);
        for (const error of errors) {
            toast.error(error, {
                cancel: {
                    label: <XIcon className="size-4" />,
                    onClick: () => {},
                },
                cancelButtonStyle: {
                    backgroundColor: 'transparent',
                    minWidth: 0,
                },
            });
        }
        textareaRef.current?.focus();
        onAttachmentsChanged?.();
    }

    function onAttachmentRemove(index: number) {
        form.setValue(
            'attachments',
            attachments.filter((_, i) => i !== index)
        );
        textareaRef.current?.focus();
        onAttachmentsChanged?.();
    }

    function onSubmit(values: z.infer<typeof formSchema>) {
        if (isSendDisabled) {
            return;
        }
        onSend?.({
            message: values.message,
            // Attachments not allowed when forwarding.
            attachments: isForwarding ? [] : values.attachments,
            replyTo: replyingTo ?? undefined,
        });
        form.reset();
        textareaRef.current?.focus();
    }

    // Reset the attachments when forwarding is toggled.
    useEffect(() => {
        if (isForwarding && attachments.length) {
            form.setValue('attachments', []);
        }
    }, [isForwarding, attachments.length, form]);

    // Focus the input when the chat box is opened.
    useEffect(() => {
        if (isOpen && autoFocus) {
            textareaRef.current?.focus();
        }
    }, [isOpen, autoFocus]);

    // Focus the input when reply mode is toggled.
    useEffect(() => {
        if (autoFocus) {
            textareaRef.current?.focus();
        }
    }, [replyingTo, autoFocus]);

    return (
        <Form {...form}>
            <form
                ref={formRef}
                id={`chat-box-input-${formId}`}
                className={cn('flex flex-col gap-[8px]', className)}
                onSubmit={form.handleSubmit(onSubmit)}
            >
                {replyingTo && (
                    <ChatInputReplyPreview
                        message={replyingTo}
                        mode={replyMode}
                        onCancel={onCancelReply}
                    />
                )}
                <FormField
                    name="message"
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="space-y-[8px]">
                            <FormControl ref={textareaRef}>
                                <Textarea
                                    // Need ! to override md:text-base class
                                    className="min-h-0 resize-none !rounded-[8px] px-[12px] py-[8px] text-[16px] leading-tight md:!text-[14px] md:!leading-tight"
                                    color="contrast"
                                    {...field}
                                    disabled={
                                        field.disabled || isNotParticipant
                                    }
                                    placeholder={
                                        !isNotParticipant
                                            ? 'Write a message...'
                                            : 'Signed in as ArchiPro staff - sending is disabled'
                                    }
                                    rows={3}
                                    autoFocus={autoFocus}
                                    autoComplete="off"
                                    onChange={(e) => {
                                        field.onChange(e);
                                        if (
                                            e.target.value.length >
                                            field.value.length
                                        ) {
                                            onTyping?.();
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            if (
                                                e.ctrlKey ||
                                                e.altKey ||
                                                e.shiftKey
                                            ) {
                                                // Ctrl, Alt, or Shift + Enter to insert a new line.
                                                e.preventDefault();
                                                form.setValue(
                                                    'message',
                                                    field.value + '\n'
                                                );
                                            } else {
                                                // Just Enter to send the message.
                                                formRef.current?.requestSubmit();
                                            }
                                        }
                                    }}
                                    onPaste={(e) => {
                                        if (isForwarding) return;
                                        const files = e.clipboardData.files;
                                        if (files.length) {
                                            e.preventDefault();
                                            onFilesAdded(files, true);
                                        }
                                    }}
                                />
                            </FormControl>
                            <FormMessage
                                className="text-[12px]"
                                variant="darker"
                            />
                        </FormItem>
                    )}
                />
                {attachments.length > 0 && (
                    <ChatInputAttachments
                        attachments={attachments}
                        onAttachmentRemove={onAttachmentRemove}
                    />
                )}
                <TypingIndicator
                    className="px-[12px]"
                    conversationID={conversationID}
                />
                <div className="flex items-center justify-between gap-[8px]">
                    <div className="flex items-center gap-[4px]">
                        {/* Attachments not allowed when forwarding. */}
                        {!isForwarding && (
                            <>
                                <Button
                                    className="border border-gray-900 hover:bg-white/20"
                                    type="button"
                                    color="transparent"
                                    size="narrow"
                                    weight="normal"
                                    disabled={isSubmitting || isNotParticipant}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                >
                                    Upload File
                                </Button>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    className="hidden"
                                    multiple
                                    onChange={(e) =>
                                        onFilesAdded(e.target.files, false)
                                    }
                                />
                            </>
                        )}
                        <GiphyPicker
                            onSelect={(_, url) => {
                                onSend?.({
                                    message: url,
                                    attachments: [],
                                    replyTo: replyingTo ?? undefined,
                                });
                            }}
                        >
                            <Button
                                className="border border-gray-900 hover:bg-white/20"
                                type="button"
                                color="transparent"
                                size="narrow"
                                weight="normal"
                                disabled={isSubmitting || isNotParticipant}
                            >
                                GIF
                            </Button>
                        </GiphyPicker>
                    </div>
                    <Button
                        className="bg-brick-700 py-2 text-white hover:bg-brick-900 hover:text-white"
                        type="submit"
                        color="primary"
                        size="larger"
                        disabled={isSendDisabled}
                        isLoading={isSubmitting}
                    >
                        {isForwarding ? 'Forward' : 'Send'}
                        <SendHorizontalIcon className="ml-2 size-4" />
                    </Button>
                </div>
            </form>
        </Form>
    );
}

/**
 * Rename a pasted file to include a timestamp, since browsers give pasted
 * images the generic name "image.png".
 * @example "image.png" -> "pasted-image-20260213-143022.png"
 */
function renamePastedImage(original: File): File {
    if (original.name !== 'image.png') {
        return original;
    }
    const now = new Date();
    const timestamp = [
        now.getFullYear(),
        String(now.getMonth() + 1).padStart(2, '0'),
        String(now.getDate()).padStart(2, '0'),
        '-',
        String(now.getHours()).padStart(2, '0'),
        String(now.getMinutes()).padStart(2, '0'),
        String(now.getSeconds()).padStart(2, '0'),
    ].join('');
    const newName = `pasted-image-${timestamp}.png`;
    return renameFile(original, newName);
}
